@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

private fun tabularSidePointerX(event: Event): Double? {
    val mouse = event as? MouseEvent
    if (mouse != null) return mouse.clientX.toDouble()
    val touch = event as? TouchEvent ?: return null
    return (touch.touches.item(0) ?: touch.changedTouches.item(0))?.clientX?.toDouble()
}

@Composable
fun TabularSideSplitScreen(
    screen1: @Composable () -> Unit,
    screen2: @Composable () -> Unit,
    storageId: String,
    initialRatio: Float = 0.60f,
    minRatio: Float = 0.25f,
    maxRatio: Float = 0.75f
) {
    val storageKey = "tabular.side.split.$storageId"
    fun storedRatio() = window.localStorage.getItem(storageKey)?.toFloatOrNull()
        ?.coerceIn(minRatio, maxRatio) ?: initialRatio.coerceIn(minRatio, maxRatio)

    var ratio by remember(storageKey) { mutableStateOf(storedRatio()) }
    var dragging by remember { mutableStateOf(false) }
    var containerRef by remember { mutableStateOf<HTMLElement?>(null) }

    if (dragging) DisposableEffect(Unit) {
        val moveHandler: (Event) -> Unit = { event ->
            val pointerX = tabularSidePointerX(event)
            val container = containerRef
            if (pointerX != null && container != null) {
                event.preventDefault()
                val rect = container.getBoundingClientRect()
                ratio = ((pointerX - rect.left) / rect.width).toFloat().coerceIn(minRatio, maxRatio)
                window.localStorage.setItem(storageKey, ratio.toString())
            }
        }
        val upHandler: (Event) -> Unit = { dragging = false }
        window.addEventListener("mousemove", moveHandler)
        window.addEventListener("mouseup", upHandler)
        window.addEventListener("touchmove", moveHandler)
        window.addEventListener("touchend", upHandler)
        window.addEventListener("touchcancel", upHandler)
        onDispose {
            window.removeEventListener("mousemove", moveHandler)
            window.removeEventListener("mouseup", upHandler)
            window.removeEventListener("touchmove", moveHandler)
            window.removeEventListener("touchend", upHandler)
            window.removeEventListener("touchcancel", upHandler)
        }
    }

    Div({
        ref { containerRef = it; onDispose { containerRef = null } }
        style { display(DisplayStyle.Flex); width(100.percent); height(100.percent); minWidth(0.px); overflow("hidden") }
    }) {
        Div({ style { flex(ratio.toString()); property("flex-basis", "0"); minWidth(0.px); overflow("hidden") } }) { screen1() }
        TabularSideDivider { dragging = true }
        Div({ style { flex((1f - ratio).toString()); property("flex-basis", "0"); minWidth(0.px); overflow("hidden") } }) { screen2() }
    }
}

@Composable
private fun TabularSideDivider(showEdges: Boolean = true, lineColor: String = "rgba(255,255,255,0.08)", onDragStart: () -> Unit) {
        Div({
            style {
                width(12.px); margin(0.px, 8.px); boxSizing("border-box"); flexShrink(0); cursor("col-resize")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("touch-action", "none"); property("user-select", "none")
                if (showEdges) {
                    property("border-left", "1px solid $lineColor")
                    property("border-right", "1px solid $lineColor")
                }
            }
            onMouseDown { onDragStart(); it.preventDefault() }
            onTouchStart { onDragStart(); it.preventDefault() }
        }) {
            Div({ style { width(3.px); height(42.px); borderRadius(999.px); backgroundColor(Color("rgba(255,255,255,0.92)")); property("box-shadow", "0 0 8px rgba(255,255,255,0.18)") } })
        }
}

@Composable
fun TabularThreeSideSplitScreen(
    screen1: @Composable () -> Unit,
    screen2: @Composable () -> Unit,
    screen3: @Composable () -> Unit,
    storageId: String,
    initialFirstRatio: Float = 1f / 3f,
    initialSecondRatio: Float = 2f / 3f,
    dividerEdges: Boolean = true,
    dividerLineColor: String = "rgba(255,255,255,0.08)"
) {
    val storageKey = "tabular.side.triple.$storageId"
    fun stored(index: Int, fallback: Float): Float = window.localStorage.getItem("$storageKey.$index")
        ?.toFloatOrNull()?.takeIf { it.isFinite() }?.coerceIn(0f, 1f) ?: fallback
    var first by remember(storageKey) { mutableStateOf(stored(1, initialFirstRatio.coerceIn(0f, 1f))) }
    var second by remember(storageKey) { mutableStateOf(stored(2, initialSecondRatio.coerceIn(0f, 1f)).coerceAtLeast(first)) }
    var dragging by remember { mutableStateOf(0) }
    var containerRef by remember { mutableStateOf<HTMLElement?>(null) }

    if (dragging != 0) DisposableEffect(dragging) {
        val activeDivider = dragging
        val moveHandler: (Event) -> Unit = { event ->
            val pointerX = tabularSidePointerX(event)
            val rect = containerRef?.getBoundingClientRect()
            if (pointerX != null && rect != null && rect.width > 56.0) {
                event.preventDefault()
                // Each shared divider occupies 28px, including its margins.
                val offset = if (activeDivider == 1) 14.0 else 42.0
                val position = ((pointerX - rect.left - offset) / (rect.width - 56.0)).toFloat().coerceIn(0f, 1f)
                if (activeDivider == 1) { first = position; second = second.coerceAtLeast(first) }
                else { second = position; first = first.coerceAtMost(second) }
                window.localStorage.setItem("$storageKey.1", first.toString())
                window.localStorage.setItem("$storageKey.2", second.toString())
            }
        }
        val upHandler: (Event) -> Unit = { dragging = 0 }
        window.addEventListener("mousemove", moveHandler)
        window.addEventListener("mouseup", upHandler)
        window.addEventListener("touchmove", moveHandler)
        window.addEventListener("touchend", upHandler)
        window.addEventListener("touchcancel", upHandler)
        onDispose {
            window.removeEventListener("mousemove", moveHandler)
            window.removeEventListener("mouseup", upHandler)
            window.removeEventListener("touchmove", moveHandler)
            window.removeEventListener("touchend", upHandler)
            window.removeEventListener("touchcancel", upHandler)
        }
    }
    Div({
        ref { containerRef = it; onDispose { containerRef = null } }
        style { display(DisplayStyle.Flex); width(100.percent); height(100.percent); minWidth(0.px); overflow("hidden") }
    }) {
        Div({ style { flex(first.toString()); property("flex-basis", "0"); minWidth(0.px); overflow("hidden") } }) { screen1() }
        TabularSideDivider(showEdges = dividerEdges, lineColor = dividerLineColor) { dragging = 1 }
        Div({ style { flex((second - first).toString()); property("flex-basis", "0"); minWidth(0.px); overflow("hidden") } }) { screen2() }
        TabularSideDivider(showEdges = dividerEdges, lineColor = dividerLineColor) { dragging = 2 }
        Div({ style { flex((1f - second).toString()); property("flex-basis", "0"); minWidth(0.px); overflow("hidden") } }) { screen3() }
    }
}
